import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
  Collapse,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { renderMessageWithCitations } from "./chatCitationUtils";
import type {
  ChatStartConversationBody,
  ChatStartConversationResponse,
  ChatMessageResponse,
  ChatSource,
} from "@services/chat/types";

interface ChatMessage {
  role: "user" | "dobby";
  content: string;
  timestamp: Date;
  sources?: ChatSource[];
}

interface DobbyChatProps {
  conversationType: "example" | "rule_update" | "shipment_question";
  initialMessage?: string;
  ruleId?: string;
  extraParams?: {
    feedId?: string;
  };
  onConversationEnd?: (result: string | null) => void;
  // Required: API client for making requests (not needed in mock mode)
  apiClient?: {
    post<TResponse, TRequest = any>(
      url: string,
      data?: TRequest,
      config?: any,
    ): Promise<TResponse>;
  };
  // Optional: Custom avatar image path
  avatarImagePath?: string;
  // Optional: Mock mode for demo
  mockMode?: boolean;
  defaultChatAnswer?: {
    dobbyResponse: string;
    sources?: ChatSource[];
  };
  // Optional: handler for opening emails from sources
  onOpenEmail?: (messageId: string, taskName?: string) => void;
}

const DobbyChat: React.FC<DobbyChatProps> = ({
  conversationType,
  initialMessage,
  ruleId,
  extraParams,
  onConversationEnd,
  apiClient,
  avatarImagePath = "/www/assets/dobby_internal_tools_icon.png",
  mockMode = false,
  defaultChatAnswer,
  onOpenEmail,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const sendMockResponse = useCallback(() => {
    if (!defaultChatAnswer) return;

    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          role: "dobby",
          content: defaultChatAnswer.dobbyResponse,
          timestamp: new Date(),
          sources: defaultChatAnswer.sources,
        },
      ]);
      setIsLoading(false);
    }, 3000);
  }, [defaultChatAnswer]);

  const startConversation = useCallback(
    async (firstMessage?: string) => {
      if (mockMode) {
        if (firstMessage) {
          setMessages([
            {
              role: "user",
              content: firstMessage,
              timestamp: new Date(),
            },
          ]);
        }
        setIsLoading(true);
        setConversationId("mock-conversation");
        sendMockResponse();
        return;
      }

      if (!apiClient) return;

      setIsLoading(true);
      try {
        const response = await apiClient.post<ChatStartConversationResponse>(
          "/chat/conversation/start/",
          {
            conversationType: conversationType,
            initialMessage: firstMessage,
            ruleId: ruleId,
            extraParams: extraParams,
          } satisfies ChatStartConversationBody,
        );

        setConversationId(response.conversationId);

        if (firstMessage) {
          setMessages([
            {
              role: "user",
              content: firstMessage,
              timestamp: new Date(),
            },
          ]);
        }

        if (response.dobbyResponse) {
          setMessages((prev) => [
            ...prev,
            {
              role: "dobby",
              content: response.dobbyResponse.dobbyResponse,
              timestamp: new Date(),
              sources: response.dobbyResponse.sources,
            },
          ]);
        }
      } catch (error) {
        console.error("Error starting conversation:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      apiClient,
      conversationType,
      ruleId,
      extraParams,
      mockMode,
      sendMockResponse,
    ],
  );

  // Initialize conversation if initial message is provided
  useEffect(() => {
    if (initialMessage && !conversationId) {
      startConversation(initialMessage);
    }
  }, [initialMessage, conversationId, startConversation]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading || conversationEnded) return;

    const userMessage = input;
    setInput("");
    setMessages((prev) => [
      ...prev,
      {
        role: "user",
        content: userMessage,
        timestamp: new Date(),
      },
    ]);
    setIsLoading(true);

    if (mockMode) {
      if (!conversationId) {
        setConversationId("mock-conversation");
      }
      sendMockResponse();
      return;
    }

    try {
      // Start conversation if not already started
      if (!conversationId) {
        await startConversation(userMessage);
        return;
      }

      if (!apiClient) return;

      // Send message to existing conversation
      const response = await apiClient.post<ChatMessageResponse>(
        `/chat/conversation/${conversationId}/chat/`,
        {
          message: userMessage,
        },
      );

      setMessages((prev) => [
        ...prev,
        {
          role: "dobby",
          content: response.dobbyResponse,
          timestamp: new Date(),
          sources: response.sources,
        },
      ]);

      if (response.conversationEnded) {
        setConversationEnded(true);
        if (onConversationEnd) {
          onConversationEnd(response.result || null);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "dobby",
          content: "Sorry, something went wrong. Please try again.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle source click
  const handleSourceClick = (source: ChatSource) => {
    if (source.type === "email" && onOpenEmail) {
      onOpenEmail(source.data.messageId, "Chat Source Email");
    }
  };

  // Handle text field click to expand when collapsed
  const handleTextFieldClick = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
  };

  // Calculate dynamic height based on messages and collapse state
  const calculateHeight = () => {
    const inputHeight = 50; // Text input area height

    if (isCollapsed || messages.length === 0) {
      return `${inputHeight}px`; // Just show text input when collapsed or no messages
    }

    return undefined;
  };

  return (
    <Paper
      className="dobby-chat-paper"
      elevation={8}
      sx={{
        position: "absolute",
        bottom: 8,
        right: "6px",
        left: "6px",
        height: calculateHeight(),
        borderRadius: "16px",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 1000,
        transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      {/* Close button - only show when there are messages and not collapsed */}
      {messages.length > 0 && !isCollapsed && (
        <Box
          sx={{
            position: "absolute",
            top: 6,
            left: 6,
            zIndex: 1001,
          }}
        >
          <IconButton
            size="small"
            onClick={() => setIsCollapsed(!isCollapsed)}
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.95)",
              width: 24,
              height: 24,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {/* Message History */}
      <Collapse in={!isCollapsed && messages.length > 0}>
        <Box
          ref={messagesContainerRef}
          sx={{
            maxHeight: "360px",
            minHeight: "40px",
            overflowY: "auto",
            p: 0.5,
            backgroundColor: "#f9f9f9",
            flex: 1,
          }}
        >
          <List dense>
            {messages.map((msg, index) => (
              <ListItem
                key={index}
                sx={{
                  justifyContent:
                    msg.role === "user" ? "flex-end" : "flex-start",
                  alignItems: "flex-start",
                  py: 0.5,
                }}
              >
                {msg.role === "dobby" && (
                  <Avatar
                    src={avatarImagePath}
                    alt="Dobby Tools Icon"
                    sx={{ marginRight: 1, width: 24, height: 24 }}
                  />
                )}
                <Paper
                  sx={{
                    p: 0.75,
                    maxWidth: msg.role === "user" ? "80%" : "75%",
                    marginLeft: msg.role === "dobby" ? "0px" : 0,
                    backgroundColor:
                      msg.role === "user" ? "#e3f2fd" : "#ffffff",
                    borderRadius:
                      msg.role === "user"
                        ? "12px 12px 4px 12px"
                        : "12px 12px 12px 4px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  }}
                >
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      wordBreak: "break-word",
                      textAlign: msg.role === "user" ? "right" : "left",
                      fontSize: "0.85rem",
                    }}
                  >
                    {msg.role === "dobby" && msg.sources
                      ? renderMessageWithCitations(
                          msg.content,
                          msg.sources,
                          handleSourceClick,
                        )
                      : msg.content}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.65rem",
                      color: "text.secondary",
                      textAlign: msg.role === "user" ? "right" : "left",
                      display: "block",
                      mt: 0.5,
                    }}
                  >
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </Typography>
                </Paper>
              </ListItem>
            ))}
            {isLoading && (
              <ListItem sx={{ justifyContent: "center", py: 1 }}>
                <CircularProgress size={20} />
              </ListItem>
            )}
            <div ref={messagesEndRef} />
          </List>
        </Box>
      </Collapse>

      {/* Input Area - Fixed height to prevent animation issues */}
      <Box
        className="dobby-chat-input-wrapper"
        sx={{
          height: "50px",
          px: 1,
          borderTop:
            messages.length > 0 && !isCollapsed ? "1px solid #e0e0e0" : "none",
          backgroundColor: "#fff",
          display: "flex",
          alignItems: "center",
          borderBottomLeftRadius: "16px",
          borderBottomRightRadius: "16px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
          }}
        >
          <TextField
            fullWidth
            multiline={false}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleTextFieldClick}
            placeholder="Ask Dobby..."
            disabled={isLoading}
            variant="outlined"
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                height: "36px",
              },
              "& .MuiOutlinedInput-input": {
                padding: "8px 12px",
              },
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            size="small"
            sx={{
              minWidth: "36px",
              width: "36px",
              height: "36px",
              borderRadius: "12px",
              p: 0,
            }}
          >
            <SendIcon fontSize="small" />
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default DobbyChat;
